import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { dynamicsPenalty } from '../../dynamics-ranges.js';

/**
 * Score display for Test Mode, showing current score out of 100.
 *
 * When Dynamics feedback is on AND the slice is eligible AND at least one onset has
 * been judged, a second line shows the running dynamics tally and the points the
 * dynamics penalty is currently costing — so the player can see why the score is
 * where it is. The line is hidden when `judged === 0`, which covers dynamics off,
 * an ineligible slice, and passes where no correct press has landed yet.
 */
@customElement('test-mode-score')
export class TestModeScore extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                flex-direction: column;
                align-items: stretch;
                background: var(--surface-light);
                padding: 2px 8px;
                border-radius: 8px;
                border: 1px solid var(--border);
                line-height: 1.15;
            }

            :host(:not([visible])) {
                display: none;
            }

            .top {
                display: flex;
                align-items: baseline;
                gap: 8px;
            }

            .score-block {
                display: inline-flex;
                align-items: baseline;
            }

            .value {
                font-size: 24px;
                font-weight: bold;
                color: var(--primary-color);
                font-family: monospace;
                min-width: 3ch;
                text-align: right;
            }

            :host([final]) .value {
                color: var(--success-color);
            }

            .max {
                font-size: 14px;
                color: var(--text-secondary);
                font-family: monospace;
            }

            .pauses {
                font-size: 12px;
                color: var(--text-secondary);
                font-family: monospace;
            }

            :host([mode="practice"]) .pauses {
                display: none;
            }

            /* Mirrors .dynamics-stats in history-calendar.ts so the two places read
               as one system. */
            .dynamics-stats {
                font-size: 11px;
                color: var(--text-muted);
                font-family: monospace;
                white-space: nowrap;
            }

            .dynamics-stats .penalty {
                color: var(--warning-color, #f59e0b);
                margin-left: 4px;
            }
        `,
    ];

    @property({ type: Number })
    score = 0;

    @property({ type: Number })
    pauseCount = 0;

    @property({ type: Boolean, reflect: true })
    final = false;

    @property({ type: Boolean, reflect: true })
    visible = false;

    /** Which mode's score is displayed — affects label only. */
    @property({ type: String, reflect: true })
    mode: 'test' | 'practice' = 'test';

    /** Count of onsets the dynamics judge has resolved to a verdict so far. */
    @property({ type: Number })
    judged = 0;

    /** Of those, how many were too-loud or too-soft. */
    @property({ type: Number })
    outOfRange = 0;

    private _renderDynamics() {
        if (this.judged <= 0) return nothing;
        // Compact form so the score card stays no wider than the top row and the
        // conductor header keeps to one line at iPad Air 13-inch. The header row
        // above already contextualises the number; a bare `Dyn N%` reads as
        // dynamics-in-range and is short enough to fit under `⏸ 0`.
        const inRangePct = Math.round(((this.judged - this.outOfRange) / this.judged) * 100);
        const penalty = dynamicsPenalty(this.judged, this.outOfRange);
        return html`
            <span class="dynamics-stats"
                >Dyn ${inRangePct}%${penalty > 0
                    ? html` <span class="penalty">&minus;${penalty}</span>`
                    : nothing}</span
            >
        `;
    }

    render() {
        const displayScore = Math.floor(Math.max(0, Math.min(100, this.score)));
        return html`
            <span class="top">
                <span class="score-block"><span class="value">${displayScore}</span><span class="max">/100</span></span>
                <span class="pauses">⏸ ${this.pauseCount}</span>
            </span>
            ${this._renderDynamics()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'test-mode-score': TestModeScore;
    }
}
